(ns superadmin.id-lookup-page.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tab$default" :as Tab]
            ["@mui/material/Tabs$default" :as Tabs]
            ["@mui/material/TextField$default" :as TextField]
            [clojure.string :as str]
            [common.widgets.page :as page]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reitit.frontend.easy :as r.easy]
            [superadmin.resource.id-lookup]
            [superadmin.resource.name-search]
            [uix.core :as uix :refer [$ defui]]))

;; ID Lookup Helper Functions

(defn entity-link
  "Build link URL for an entity based on type and parent chain"
  [entity-type entity]
  (let [context {:agency-id (get-in entity [:agency :id])
                 :advertiser-id (get-in entity [:advertiser :id])
                 :campaign-id (get-in entity [:campaign :id])}]
    (case entity-type
      :agency
      (r.easy/href :agency/configuration (assoc context :agency-id (:id entity)))

      :advertiser
      (r.easy/href :advertiser/page (assoc context :advertiser-id (:id entity)))

      :campaign
      (r.easy/href :campaign/page (assoc context :campaign-id (:id entity)))

      ;; TODO: return correct route based on workflow
      :adset
      (r.easy/href :adset/ad-variants (assoc context :adset-id (:id entity))))))

(defn result-item [entity-type entity]
  (let [link (entity-link entity-type entity)
        active-text (if (:active entity) "active" "inactive")
        entity-name (str/upper-case (name entity-type))
        ;; Generate parent links - need to build full entity objects with parent data
        campaign-link (when-let [campaign (:campaign entity)]
                        ;; Campaign needs agency and advertiser for proper link
                        (entity-link :campaign (assoc campaign
                                                      :agency (:agency entity)
                                                      :advertiser (:advertiser entity))))
        advertiser-link (when-let [advertiser (:advertiser entity)]
                          ;; Advertiser needs agency for proper link
                          (entity-link :advertiser (assoc advertiser
                                                          :agency (:agency entity))))
        agency-link (when-let [agency (:agency entity)]
                      (entity-link :agency agency))]
    [:div {:style {:padding "12px"
                   :border-bottom "1px solid #f0f0f0"}}
     (if link
       [:div {:style {:display "block"}}
        [:a {:href link
             :target "_blank"
             :style {:text-decoration "none"
                     :color "#1976d2"}}
         [:span {:style {:font-weight "600" :color "#666"}} entity-name]
         [:span {:style {:margin "0 8px" :color "#999"}} "|"]
         [:span {:style {:font-weight "500"}} (:id entity)]
         [:span {:style {:margin "0 8px"}} "-"]
         [:span (:name entity)]
         [:span {:style {:margin-left "8px"
                         :padding "2px 6px"
                         :border-radius "3px"
                         :font-size "0.85em"
                         :background-color (if (:active entity) "#e8f5e9" "#ffebee")
                         :color (if (:active entity) "#2e7d32" "#c62828")}}
          active-text]]
        (when-let [campaign (:campaign entity)]
          [:span {:style {:margin-left "12px" :font-size "0.9em"}}
           "→ Campaign: "
           (if campaign-link
             [:a {:href campaign-link
                  :target "_blank"
                  :style {:color "#1976d2" :text-decoration "none"}
                  :on-click #(.stopPropagation %)}
              (:name campaign)]
             [:span {:style {:color "#666"}} (:name campaign)])])
        (when-let [advertiser (:advertiser entity)]
          [:span {:style {:margin-left "12px" :font-size "0.9em"}}
           "→ Advertiser: "
           (if advertiser-link
             [:a {:href advertiser-link
                  :target "_blank"
                  :style {:color "#1976d2" :text-decoration "none"}
                  :on-click #(.stopPropagation %)}
              (:name advertiser)]
             [:span {:style {:color "#666"}} (:name advertiser)])])
        (when-let [agency (:agency entity)]
          [:span {:style {:margin-left "12px" :font-size "0.9em"}}
           "→ Agency: "
           (if agency-link
             [:a {:href agency-link
                  :target "_blank"
                  :style {:color "#1976d2" :text-decoration "none"}
                  :on-click #(.stopPropagation %)}
              (:name agency)]
             [:span {:style {:color "#666"}} (:name agency)])])]
       [:div {:style {:color "#999"}}
        [:span {:style {:font-weight "600"}} entity-name]
        " | " (:id entity) " - " (:name entity)
        " (no link - missing parent data)"])]))

(defn render-results [results]
  (let [all-entities (concat
                      (map #(vector :agency %) (:agencies results))
                      (map #(vector :advertiser %) (:advertisers results))
                      (map #(vector :campaign %) (:campaigns results))
                      (map #(vector :adset %) (:adsets results)))
        truncated (:truncated results)
        has-truncated? (some true? (vals truncated))]
    (if (empty? all-entities)
      [:p {:style {:color "#666" :font-style "italic"}} "No entities found"]
      [:div {:style {:margin-top "16px"}}
       (when has-truncated?
         [:div {:style {:padding "12px"
                        :margin-bottom "16px"
                        :background-color "#fff3cd"
                        :border "1px solid #ffc107"
                        :border-radius "4px"
                        :color "#856404"}}
          [:strong "Note: "] "Some results may be truncated (max 50 per entity type). "
          "Try refining your search for more specific results."])
       (for [[entity-type entity] all-entities]
         ^{:key (str (name entity-type) "-" (:id entity))}
         [result-item entity-type entity])])))

(defn id-lookup-content []
  (let [search-term @(rf/subscribe [:superadmin/id-lookup-term])
        results @(rf/subscribe [:superadmin/id-lookup-results])
        loading? @(rf/subscribe [:superadmin/id-lookup-loading?])]
    [:div
     [:div {:style {:display "flex" :gap "8px" :margin-bottom "24px"}}
      ($ TextField
         {:value search-term
          :onChange #(rf/dispatch [:superadmin/set-id-lookup-term (-> % .-target .-value)])
          :onKeyPress #(when (= "Enter" (.-key %))
                         (rf/dispatch [:superadmin/search-id]))
          :onFocus #(-> % .-target .select)
          :disabled loading?
          :fullWidth true
          :autoFocus true
          :placeholder "Enter an agency, advertiser, campaign, or adset ID"})
      ($ Button
         {:variant "contained"
          :onClick #(rf/dispatch [:superadmin/search-id])
          :disabled loading?
          :style {:min-width "120px"}}
         (if loading? "Searching..." "Search"))]

     (when loading?
       [:div {:style {:margin-top "16px" :text-align "center"}}
        ($ CircularProgress {:size 32})])

     (when (and (not loading?) (nil? results))
       [:p {:style {:margin-top "16px" :color "#999" :font-style "italic"}}
        "Enter an ID and click Search to find entities"])

     (when (and results (not loading?))
       [:div {:style {:margin-top "24px"}}
        [:h4 {:style {:margin-bottom "16px"}} "Results"]
        [render-results results]])]))

(defn name-search-content []
  (let [search-term @(rf/subscribe [:superadmin/name-search-term])
        results @(rf/subscribe [:superadmin/name-search-results])
        loading? @(rf/subscribe [:superadmin/name-search-loading?])]
    [:div
     [:div {:style {:display "flex" :gap "8px" :margin-bottom "24px"}}
      ($ TextField
         {:value search-term
          :onChange #(rf/dispatch [:superadmin/set-name-search-term (-> % .-target .-value)])
          :onKeyPress #(when (= "Enter" (.-key %))
                         (rf/dispatch [:superadmin/search-name]))
          :onFocus #(-> % .-target .select)
          :disabled loading?
          :fullWidth true
          :placeholder "Enter agency, advertiser, campaign, or adset name"})
      ($ Button
         {:variant "contained"
          :onClick #(rf/dispatch [:superadmin/search-name])
          :disabled loading?
          :style {:min-width "120px"}}
         (if loading? "Searching..." "Search"))]

     (when loading?
       [:div {:style {:margin-top "16px" :text-align "center"}}
        ($ CircularProgress {:size 32})])

     (when (and (not loading?) (nil? results))
       [:p {:style {:margin-top "16px" :color "#999" :font-style "italic"}}
        "Enter a name and click Search to find entities (case-insensitive, partial match)"])

     (when (and results (not loading?))
       [:div {:style {:margin-top "24px"}}
        [:h4 {:style {:margin-bottom "16px"}} "Results"]
        [render-results results]])]))

(defui search-page-content []
  (let [[tab set-tab] (uix/use-state 0)]
    ($ Paper {:sx #js {:px 3 :py 2}}
       ($ Stack {:gap 3}
          ($ Tabs
             {:value tab
              :onChange (fn [_e v]
                          (set-tab v))
              :sx #js {:borderBottom 1 :borderColor "divider"}}
             ($ Tab {:label "Search by ID"})
             ($ Tab {:label "Search by Name"}))
          (case tab
            0 (r/as-element [id-lookup-content])
            1 (r/as-element [name-search-content]))))))

(defn id-lookup-page []
  ($ page/page
     ($ page/page-header
        {:title "Entity Search"
         :description "Search for entities by ID or name across agencies, advertisers, campaigns, and adsets"})
     ($ page/content-padding
        ($ search-page-content))))
